'use client';
import { DynamicIcon } from '@curology/ui-components-web/react/Icon/DynamicIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import React from 'react';
import { type ItemValue, type QuestionRadiogroupModel } from 'survey-core';

import { autoAdvancingAtom } from '../../atoms/autoAdvancingAtom';

// This global state is necessary for state to be shared between radio items of a radio group
// It is included here because rendering each radio item has been abstracted away by SurveyJS
let arrowPressed = false;

const RadioItemHoriz = ({
  question,
  item,
  isChecked,
}: {
  question: QuestionRadiogroupModel;
  item: ItemValue;
  isChecked: boolean;
}) => {
  const [isAutoAdvancing, setIsAutoAdvancing] = useAtom(autoAdvancingAtom);
  const handleOnChange = () => {
    if (isAutoAdvancing) {
      return;
    }
    question.clickItemHandler(item);
    if (!arrowPressed) {
      const timeout =
        question.surveyModel.getPropertyValue('autoAdvanceDelay') || 250;
      setIsAutoAdvancing(true);

      setTimeout(() => {
        question.surveyModel.nextPage() ||
          question.surveyModel.completeLastPage();

        setIsAutoAdvancing(false);
      }, timeout);
    }
    arrowPressed = false;
  };

  const handleKeyDownChange: React.KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    if (event.key.includes('Arrow')) {
      arrowPressed = true;
    }
  };

  return (
    <div role="presentation" className="cur-text-primary">
      <input
        role="option"
        aria-selected={isChecked}
        className="cur-peer cur-sr-only"
        id={question.getItemId(item)}
        type="radio"
        tabIndex={0}
        name={question.questionName}
        defaultChecked={isChecked}
        value={item.value}
        onChange={handleOnChange}
        onKeyDown={handleKeyDownChange}
        aria-label={item.type}
      />
      <label
        htmlFor={question.getItemId(item)}
        className={clsx(
          'cur-grid cur-justify-items-center cur-cursor-pointer cur-ring-4 peer-focus-visible:cur-ring-offset-border-brand-selected peer-focus-visible:cur-ring-offset-2',
          item.description ? 'cur-py-5' : 'cur-py-6',
          isChecked ? 'cur-ring-surface-brand' : ' cur-ring-transparent',
        )}
      >
        {item.inputIcon && (
          <DynamicIcon
            name={item.inputIcon}
            size={question.iconSize}
            className="cur-flex-shrink-0 cur-mb-10"
          />
        )}
        {item.inputIcon2 && (
          <DynamicIcon
            name={item.inputIcon2}
            size={question.iconSize}
            className="cur-flex-shrink-0 cur-mb-10"
          />
        )}
        <div className="cur-mb-6">
          <Typography size="body" weight="bold">
            {item.text}
          </Typography>
          {item.description && (
            <Typography size="meta">{item.description}</Typography>
          )}
        </div>
      </label>
    </div>
  );
};

export default RadioItemHoriz;
